import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'

import type {
  HouseAndLandConfigurationInterface,
  LotFilterConfiguration,
  LotFilterInterface,
  PriceRange,
  ProjectIdentity,
  PropertyConfigurationInterface,
  RootStateTypeExtra,
} from '@store/types'

import ToggleSwitch from '@components/toggle-switch'
import VerticalScrollContainer from '@components/vertical-scroll-container'

import { SummaryCollectionInterface } from '@api/houseAndLand'
import { FilterRemote } from '@api/white-label'

import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { CloseSvg } from '@svg/react'

import { MinMaxInterface } from '@adUtilities/types/common'
import { FilterOptionsConfigurations } from '@adUtilities/types/houseAndLand'

import DropDown from '../dropdown'
import { CheckboxInput } from '../form-controls'
import { DEFAULT_ANY_OPTION } from './constants'
import FILTER_INITIAL_STATE from './filterStateHouseAndLand'
import RangeOptions from './range-options'

interface FilterProps {
  isOpen: boolean
  toggle: (arg: boolean) => void
  activePrecinct: string
  firebaseLotFilter: LotFilterInterface
  prices: PriceRange
  showPrice: boolean
  hideStages?: boolean
  isStageFetching?: boolean
  aspects: Array<string>
  propertyTypeOptions: Array<string>
  packageSummary: SummaryCollectionInterface | null
  propertyConfig: PropertyConfigurationInterface
  houseAndLandConfig: HouseAndLandConfigurationInterface
  projectIdentity: ProjectIdentity
  firebaseActiveStage: string
  filterWhiteLabel: FilterRemote
}
interface FilterPayload {
  [key: string]: string | boolean
}
function HouseAndLandFilter({
  isOpen,
  toggle,
  activePrecinct,
  firebaseLotFilter,
  prices,
  showPrice,
  aspects,
  propertyTypeOptions,
  propertyConfig,
  houseAndLandConfig,
  projectIdentity,
  firebaseActiveStage,
  hideStages,
  isStageFetching,
  packageSummary,
  filterWhiteLabel,
}: FilterProps) {
  const filterRef = useRef<HTMLDivElement>(null)
  const {
    lotFrontage: lotFrontageOptions,
    lotArea: lotAreaOptions,
    storey: storeyOptions,
    builder: builderOptions,
    packageFilter,
  } = houseAndLandConfig || {}
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const activePackageSummary = packageSummary?.[activePrecinct]
  const [activeStage, setActiveStage] = useState(firebaseActiveStage)
  const [checkAnyStage, setAnyStage] = useState(firebaseLotFilter.anyStage)

  const [configurations, setConfigurations] =
    useState<FilterOptionsConfigurations>(firebaseLotFilter.configurations)
  const [lotConfigurations, setLotConfigurations] =
    useState<LotFilterConfiguration>(firebaseLotFilter.lotConfiguration)
  const [builder, setBuilder] = useState<Array<string>>(
    firebaseLotFilter.builder
  )
  const [storey, setStorey] = useState<Array<number>>(firebaseLotFilter.storey)
  const [packages, setPackages] = useState<Array<string>>(
    firebaseLotFilter.packages
  )

  const [showAvailable, setShowAvailable] = useState(
    firebaseLotFilter.showOnlyAvailable
  )

  const stageOptions = useMemo(
    () =>
      Object.keys(activePackageSummary?.stages ?? {})?.map((label) => ({
        label: `Stage ${label}`,
        value: label,
      })),
    [activePrecinct, activePackageSummary]
  )

  const packageOptions = useMemo(
    () => activePackageSummary?.packages?.map((pkg) => pkg.name) ?? [],
    [activePackageSummary]
  )

  const handleLotConfigurationChange = (configs: LotFilterConfiguration) =>
    firebaseControlQuery.update({
      [`houseAndLand.lotFilter.lotConfiguration`]: configs,
    })

  const handleConfigurationChange = (configs: FilterOptionsConfigurations) =>
    firebaseControlQuery.update({
      [`houseAndLand.lotFilter.configurations`]: configs,
    })

  const handleAnyStage = (checked: boolean) =>
    firebaseControlQuery.update({
      [`houseAndLand.lotFilter.anyStage`]: checked,
    })

  const handleAspectOnChange = (value: string) => {
    if (value === DEFAULT_ANY_OPTION) {
      firebaseControlQuery.update({
        [`houseAndLand.lotFilter.lotConfiguration.aspects`]: [],
      })
      return
    }

    const lotAspect: string[] = lotConfigurations?.aspects

    if (!lotAspect) {
      return
    }

    let filterAspect: string[] = []
    if (lotAspect.includes(value)) {
      filterAspect = lotAspect.filter((aspect) => aspect !== value)
    } else {
      filterAspect = [...lotAspect, value]
    }
    firebaseControlQuery.update({
      [`houseAndLand.lotFilter.lotConfiguration.aspects`]: filterAspect,
    })
  }

  const handleTypeOnChange = (value: string) => {
    const { propertyTypes } = lotConfigurations
    if (!propertyTypes) return

    const updatedPropertyTypes = propertyTypes.includes(value)
      ? propertyTypes.filter((propertyType) => propertyType !== value)
      : [...propertyTypes, value]

    firebaseControlQuery.update({
      [`houseAndLand.lotFilter.lotConfiguration.propertyTypes`]:
        updatedPropertyTypes,
    })
  }

  const handlePackageOnChange = (value: string) => {
    const updatedPackages = packages.includes(value)
      ? packages.filter((pkg) => pkg !== value)
      : [...packages, value]

    firebaseControlQuery.update({
      [`houseAndLand.lotFilter.packages`]: updatedPackages,
    })
  }

  const handleStoreyChange = (value: number) => {
    if (storey.includes(value)) {
      firebaseControlQuery.update({
        [`houseAndLand.lotFilter.storey`]: storey.filter(
          (item) => item !== value
        ),
      })
    } else {
      firebaseControlQuery.update({
        [`houseAndLand.lotFilter.storey`]: [...storey, value],
      })
    }
  }

  const handleBuilderChange = (value: string) => {
    if (builder.includes(value)) {
      firebaseControlQuery.update({
        [`houseAndLand.lotFilter.builder`]: builder.filter(
          (item) => item !== value
        ),
      })
    } else {
      firebaseControlQuery.update({
        [`houseAndLand.lotFilter.builder`]: [...builder, value],
      })
    }
  }

  const handleShowAvailable = (checked: boolean) =>
    firebaseControlQuery.update({
      [`houseAndLand.lotFilter.showOnlyAvailable`]: checked,
    })

  const applyFilter = () => {
    const filterPayload: FilterPayload = {
      [`houseAndLand.lotFilter.apply`]: true,
    }
    if (!hideStages && stageOptions.length > 1) {
      filterPayload['houseAndLand.activeStageId'] = activeStage
    }
    firebaseControlQuery.update(filterPayload)
    toggle(false)
  }

  const resetFilter = async () => {
    firebaseControlQuery.update({
      [`houseAndLand.lotFilter`]: FILTER_INITIAL_STATE,
    })
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      filterRef.current &&
      !filterRef.current.contains(event.target as Node) &&
      isOpen
    ) {
      toggle(false)
    }
  }

  const hasAnyPropertyConfig = useMemo(
    () => Object.values(propertyConfig).some((property) => property.length > 0),
    [propertyConfig]
  )

  useEffect(() => {
    setConfigurations(firebaseLotFilter.configurations)
    setLotConfigurations(firebaseLotFilter.lotConfiguration)
    setBuilder(firebaseLotFilter.builder)
    setStorey(firebaseLotFilter.storey)
    setShowAvailable(firebaseLotFilter.showOnlyAvailable)
    setAnyStage(firebaseLotFilter.anyStage)
    setPackages(firebaseLotFilter.packages)
  }, [firebaseLotFilter])

  useEffect(() => {
    if (isOpen && firebaseLotFilter?.apply) {
      firebaseControlQuery.update({
        [`houseAndLand.lotFilter.apply`]: false,
      })
    }
  }, [firebaseLotFilter, activeStage, activePrecinct])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleOutsideClick, false)
    }, 0)
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [isOpen])

  return (
    <div
      ref={filterRef}
      className={`fixed inset-y-0 right-0 z-50 h-screen w-[20rem] rounded-bl-lg rounded-tl-lg bg-secondaryColour p-5 shadow-standard ipad-pro:w-[25rem] ipad-mini-land:w-[25rem] ${
        isOpen ? '' : 'translate-x-full'
      } transition-transform duration-500`}
    >
      <div className="flex h-screen flex-col text-mainColour">
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <h2 className="text-title font-medium">Filters</h2>
            <button
              className="cursor-pointer"
              type="button"
              onClick={() => {
                toggle(false)
              }}
            >
              <CloseSvg className="mr-2 h-4 w-4" strokeColor="currentColor" />
            </button>
          </div>
          <button
            className="text-xl font-normal text-[#808080] underline opacity-60"
            type="button"
            onClick={resetFilter}
          >
            Reset filters
          </button>
        </div>
        <VerticalScrollContainer className="mt-5 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-5">
            {!hideStages && isStageFetching && (
              <div className="flex flex-col gap-1">
                <div className="h-8 w-20 rounded-lg bg-slate-700/30">
                  <div className="shimmer-container relative !p-2 opacity-50"></div>
                </div>
                <div className="h-10 w-full rounded-lg bg-slate-700/30">
                  <div className="shimmer-container relative opacity-50"></div>
                </div>
                <div className="mt-2 h-4 w-32 rounded-lg bg-slate-700/30">
                  <div className="shimmer-container relative !p-2 opacity-50"></div>
                </div>
              </div>
            )}

            {stageOptions?.length > 1 && !hideStages && !isStageFetching && (
              <div className="flex flex-col gap-2.5">
                <span className="text-default font-medium uppercase">
                  {filterWhiteLabel.stageDropdownLabel}
                </span>
                <DropDown
                  onSelect={(value) => {
                    setActiveStage(value)
                  }}
                  disabled={checkAnyStage}
                  items={stageOptions}
                  value={activeStage}
                  className={checkAnyStage ? 'opacity-60' : ''}
                />
                <div className="inline-flex items-center gap-3">
                  <ToggleSwitch
                    key="offlineToggleSwitch"
                    id="offlineToggleSwitch"
                    onChangeCallback={(e) => {
                      handleAnyStage(e.target.checked)
                    }}
                    initialState={checkAnyStage}
                    onColor="bg-mainColour"
                    offColor="bg-grayColour"
                  />
                  <span className="text-xl text-mainColour">Any Stage</span>
                </div>
              </div>
            )}

            {showPrice && (
              <RangeOptions
                field="price"
                options={prices}
                range={lotConfigurations.price}
                handleChange={(configValue: MinMaxInterface) => {
                  handleLotConfigurationChange({
                    ...lotConfigurations,
                    price: configValue,
                  })
                }}
              />
            )}

            {hasAnyPropertyConfig && (
              <span className="text-default font-medium uppercase">
                {filterWhiteLabel.configuration}
              </span>
            )}

            {Object.entries(propertyConfig)?.map(([field, value]) => {
              const range =
                configurations[field as keyof FilterOptionsConfigurations]
              return (
                range &&
                value?.length > 0 && (
                  <RangeOptions
                    field={field}
                    key={field}
                    options={value as unknown as Array<string | number>}
                    range={
                      configurations?.[
                        field as keyof FilterOptionsConfigurations
                      ] || {
                        min: DEFAULT_ANY_OPTION,
                        max: DEFAULT_ANY_OPTION,
                      }
                    }
                    handleChange={(configValue: MinMaxInterface) => {
                      handleConfigurationChange({
                        ...configurations,
                        [field as keyof FilterOptionsConfigurations]:
                          configValue,
                      })
                    }}
                  />
                )
              )
            })}

            {(lotFrontageOptions?.length || 0) > 0 && (
              <RangeOptions
                field="lotFrontage"
                options={lotFrontageOptions || []}
                range={lotConfigurations.frontage}
                handleChange={(configValue: MinMaxInterface) => {
                  handleLotConfigurationChange({
                    ...lotConfigurations,
                    frontage: configValue,
                  })
                }}
              />
            )}

            {(lotAreaOptions?.length || 0) > 0 && (
              <RangeOptions
                field="lotArea"
                options={lotAreaOptions || []}
                range={lotConfigurations.area}
                handleChange={(configValue: MinMaxInterface) => {
                  handleLotConfigurationChange({
                    ...lotConfigurations,
                    area: configValue,
                  })
                }}
              />
            )}

            {propertyTypeOptions?.length > 0 && (
              <div className="flex flex-col gap-2.5">
                <span className="text-default font-medium uppercase">Type</span>
                <div className="grid grid-cols-1 gap-2.5">
                  {propertyTypeOptions?.map((propertyType: string) => {
                    const isPropertyTypeSelected =
                      lotConfigurations?.propertyTypes?.includes(
                        propertyType.toLowerCase()
                      )

                    return (
                      <button
                        type="button"
                        key={propertyType}
                        onClick={() => {
                          handleTypeOnChange(propertyType.toLowerCase())
                        }}
                        className={`rounded-lg py-2 text-center text-default font-medium ${
                          isPropertyTypeSelected
                            ? 'bg-gradient text-white'
                            : 'bg-neutralColour'
                        }`}
                      >
                        {propertyType}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {(storeyOptions?.length || 0) > 0 && (
              <div className="flex flex-col gap-2.5">
                <span className="text-default font-medium uppercase">
                  Storey
                </span>
                <div className="grid grid-cols-1 gap-2.5">
                  {storeyOptions?.map(({ label, value }) => (
                    <button
                      type="button"
                      key={label}
                      onClick={() => {
                        handleStoreyChange(value)
                      }}
                      className={`rounded-lg py-2 text-center text-default font-medium ${
                        storey?.includes(value)
                          ? 'bg-gradient text-white'
                          : 'bg-neutralColour'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {aspects?.length > 0 && (
              <div className="flex flex-col gap-2.5">
                <span className="text-default font-medium uppercase">
                  Aspect
                </span>
                <DropDown
                  onSelect={(item) => handleAspectOnChange(item)}
                  items={[
                    { value: DEFAULT_ANY_OPTION, label: DEFAULT_ANY_OPTION },
                    ...aspects.map((item) => ({
                      value: item,
                      label: item,
                    })),
                  ]}
                  value={
                    lotConfigurations?.aspects?.length === 0
                      ? DEFAULT_ANY_OPTION
                      : lotConfigurations?.aspects?.join(', ')
                  }
                />
              </div>
            )}

            {(builderOptions?.length || 0) > 0 && (
              <div className="flex flex-col gap-2.5">
                <span className="text-default font-medium uppercase">
                  Builder
                </span>
                <div className="grid grid-cols-1 gap-2.5">
                  {builderOptions?.map((builderValue: string) => (
                    <button
                      type="button"
                      key={builderValue}
                      onClick={() => {
                        handleBuilderChange(builderValue.toLowerCase())
                      }}
                      className={`rounded-lg py-2 text-center text-default font-medium ${
                        builder?.includes(builderValue.toLowerCase())
                          ? 'bg-gradient text-white'
                          : 'bg-neutralColour'
                      }`}
                    >
                      {builderValue}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {packageFilter && packageOptions?.length > 0 && (
              <div className="flex flex-col gap-2.5">
                <span className="text-default font-medium uppercase">
                  Packages
                </span>
                <div className="grid grid-cols-1 gap-2.5">
                  {packageOptions?.map((packageName: string) => {
                    const isPackageSelected = packages?.includes(
                      packageName.toLowerCase()
                    )

                    return (
                      <button
                        type="button"
                        key={packageName}
                        onClick={() => {
                          handlePackageOnChange(packageName.toLowerCase())
                        }}
                        className={`rounded-lg py-2 text-center text-default font-medium ${
                          isPackageSelected
                            ? 'bg-gradient text-white'
                            : 'bg-neutralColour'
                        }`}
                      >
                        {packageName}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </VerticalScrollContainer>
        <div className="mb-8 mt-4">
          <div className="flex items-center gap-2">
            <CheckboxInput
              initialState={showAvailable}
              onChangeCallback={(e) => {
                handleShowAvailable(e.target.checked)
              }}
              onColor="bg-mainColour"
              offColor="bg-neutralColour"
              id="availableResidence"
            />
            {filterWhiteLabel.toggleText}
          </div>
          <div className="mt-2">
            <button
              type="button"
              onClick={applyFilter}
              disabled={firebaseLotFilter?.apply}
              className={`w-full rounded-lg bg-gradient p-4 text-center text-subHeading font-medium text-white shadow-standard ${
                firebaseLotFilter?.apply
                  ? 'cursor-not-allowed opacity-40'
                  : 'opacity-100'
              }`}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default connect(
  ({
    projectIdentity: {
      prices,
      showPrice,
      propertyConfig,
      houseAndLandConfig,
      aspects,
      unitTypes,
    },
    projectIdentity,
    houseAndLand: { packageSummary },
    whiteLabel,
  }: RootStateTypeExtra) => ({
    projectIdentity,
    prices,
    showPrice,
    propertyConfig,
    houseAndLandConfig,
    aspects,
    packageSummary,
    filterWhiteLabel: mapWhiteLabelsWithDefaults(whiteLabel, true).remote
      .filter,
    propertyTypeOptions: unitTypes,
  })
)(HouseAndLandFilter)
